import { redirectDocument } from 'react-router';

import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import { cmsApiServerFetchFile } from '#/modules/cms/files/api/cmsApiFetchFile';
import { cmsViewFileRoute } from '#/modules/cms/files/routing/cmsFiles.routes';
import { DynamicNotFoundPage } from '#/pages/NotFound/DynamicNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getAppRouteSearchParams } from '#/routing/utils/getAppRouteSearchParams';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';

import { CmsViewFilePage } from './CmsViewFilePage';

export async function loader({
  request,
  url,
  context,
  params: { '*': filename },
}: HorizonAppRouteLoaderArgs<typeof cmsViewFileRoute>) {
  const { download } = getAppRouteSearchParams({
    url,
    route: cmsViewFileRoute,
  });

  const fileDetails = await cmsApiServerFetchFile({ request, context })(
    filename,
    download === 'true',
  ).catch(() => null);

  if (!fileDetails) return idealistNotFound({ filename: null });

  return fileDetails.displayPdf
    ? { filename }
    : redirectDocument(fileDetails.url, { status: 302 /* HTTP_FOUND */ });
}

export default function CmsViewFileHorizonRoute({
  loaderData: { filename },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  if (!filename) return <DynamicNotFoundPage />;

  return (
    <RouteModule title={i18n('Documents')}>
      <CmsViewFilePage filename={filename} />
    </RouteModule>
  );
}
